import { LibraryFilterQueryValue, LibraryObjectFilterParamValue } from './libraryObjectParams';
import { DEVTOOLS_PATH, DevToolsModeParam, NETWORK_PATH } from './playbackmodes';
import { QueryParamValidator, ROOT_ROUTE } from './utils';
import {
  heatmapQueryParamValidation,
  spaceQueryParamValidation,
  opportunitiesQueryParamValidation,
} from './validation';

// Route definitions
export const BASE_ROUTE = ROOT_ROUTE.chain('/ui');
export const ORG_ROUTE = BASE_ROUTE.chain('/:orgId');

export type HomeQueryParams = {
  templateGallery?: boolean; // If true, template gallery is opened
};
export type LibraryQueryParams = {
  filterBy?: LibraryFilterQueryValue;
  type?: LibraryObjectFilterParamValue;
};
export const HOME_ROUTE = ORG_ROUTE.chain('/home').queryParams<HomeQueryParams>();
export const EXPLORE_ROUTE = ORG_ROUTE.chain('/explore');
export const TERMS_ROUTE = ORG_ROUTE.chain('/termsandconditions');
export const SETUP_ROUTE = ORG_ROUTE.chain('/setup/:step');
export const SETUP_HOME_ROUTE = ORG_ROUTE.chain('/homeSetup');
export const LIBRARY_ROUTE = ORG_ROUTE.chain('/library').queryParams<LibraryQueryParams>();
export const PRIVATE_LIBRARY_ROUTE =
  LIBRARY_ROUTE.chain('/private').queryParams<LibraryQueryParams>();

// Highlights
export const HIGHLIGHTS_BASE = ORG_ROUTE.chain('/highlights');
export const HIGHLIGHTS_ROUTE = HIGHLIGHTS_BASE.chain('/:email');
export const HIGHLIGHTS_CURSOR_ROUTE = HIGHLIGHTS_ROUTE.chain('/cursor/:cursor');

// Conversions
export const CONVERSIONS_BASE_ROUTE = ORG_ROUTE.chain('/conversions');
export const CONVERSIONS_UNSAVED_ROUTE = ORG_ROUTE.chain('/conversions/new');
export const CONVERSIONS_ANALYSIS_ROUTE = CONVERSIONS_BASE_ROUTE.chain('/analysis/:analysisId');
export const CONVERSIONS_HOME_ROUTE = CONVERSIONS_BASE_ROUTE.chain('/welcome');
export const CONVERSIONS_SNAPSHOT_ROUTE = CONVERSIONS_BASE_ROUTE.chain('/snapshot/:snapshotId');
export const CONVERSIONS_RESULTS_WITH_FUNNEL_ROUTE =
  CONVERSIONS_BASE_ROUTE.chain('/funnel/:funnelId');
export const CONVERSIONS_RESULTS_WITH_FUNNEL_AND_SEGMENT_ROUTE =
  CONVERSIONS_RESULTS_WITH_FUNNEL_ROUTE.chain('/segment/:segmentId');

// Dashboards
export const DASHBOARDS_BASE_ROUTE = ORG_ROUTE.chain('/dashboards');
export const DASHBOARDS_BASE_WITH_HELP_ROUTE = DASHBOARDS_BASE_ROUTE.chain('/help');
export const DASHBOARD_ROUTE = DASHBOARDS_BASE_ROUTE.chain('/:id');
export const DASHBOARD_CREATE_ROUTE = DASHBOARDS_BASE_ROUTE.chain('/create');
export const DASHBOARD_CREATE_ADD_ROUTE = DASHBOARD_CREATE_ROUTE.chain('/add');
export const DASHBOARD_WITH_HELP_ROUTE = DASHBOARD_ROUTE.chain('/help');
export const DASHBOARD_ADD_ROUTE = DASHBOARD_ROUTE.chain('/add');
export const DASHBOARD_EDIT_ROUTE = DASHBOARD_ROUTE.chain('/edit');
export const DASHBOARD_REARRANGE_ROUTE = DASHBOARD_ROUTE.chain('/rearrange');

export const DASHBOARD_CARD_ADD_ROUTE = DASHBOARD_ROUTE.chain('/card/new/:cardType');
export const DASHBOARD_CARD_ADD_NEW_METRIC_ROUTE = DASHBOARD_CARD_ADD_ROUTE.chain('/metric/new');
export const DASHBOARD_CARD_ADD_SAVED_METRIC_ROUTE =
  DASHBOARD_CARD_ADD_ROUTE.chain('/metric/edit/:metricId');
export const DASHBOARD_CARD_ADD_NEW_FUNNEL_ROUTE = DASHBOARD_CARD_ADD_ROUTE.chain('/funnel/new');
export const DASHBOARD_CARD_ADD_SAVED_FUNNEL_ROUTE =
  DASHBOARD_CARD_ADD_ROUTE.chain('/funnel/edit/:funnelId');
export const DASHBOARD_CARD_ADD_NEW_SEGMENT_ROUTE = DASHBOARD_CARD_ADD_ROUTE.chain('/segment/new');
export const DASHBOARD_CARD_ADD_SAVED_SEGMENT_ROUTE = DASHBOARD_CARD_ADD_ROUTE.chain(
  '/segment/edit/:segmentId',
);
export const DASHBOARD_CARD_ADD_CUSTOM_SEGMENT_ROUTE =
  DASHBOARD_CARD_ADD_ROUTE.chain('/segment/custom');

export const DASHBOARD_CARD_EDIT_ROUTE = DASHBOARD_ROUTE.chain('/card/edit/:cardId');
export const DASHBOARD_CARD_EDIT_NEW_METRIC_ROUTE = DASHBOARD_CARD_EDIT_ROUTE.chain('/metric/new');
export const DASHBOARD_CARD_EDIT_SAVED_METRIC_ROUTE =
  DASHBOARD_CARD_EDIT_ROUTE.chain('/metric/edit/:metricId');
export const DASHBOARD_CARD_EDIT_NEW_FUNNEL_ROUTE = DASHBOARD_CARD_EDIT_ROUTE.chain('/funnel/new');
export const DASHBOARD_CARD_EDIT_SAVED_FUNNEL_ROUTE =
  DASHBOARD_CARD_EDIT_ROUTE.chain('/funnel/edit/:funnelId');
export const DASHBOARD_CARD_EDIT_NEW_SEGMENT_ROUTE =
  DASHBOARD_CARD_EDIT_ROUTE.chain('/segment/new');
export const DASHBOARD_CARD_EDIT_SAVED_SEGMENT_ROUTE = DASHBOARD_CARD_EDIT_ROUTE.chain(
  '/segment/edit/:segmentId',
);
export const DASHBOARD_CARD_EDIT_CUSTOM_SEGMENT_ROUTE =
  DASHBOARD_CARD_EDIT_ROUTE.chain('/segment/custom');

export const DASHBOARD_CARD_DETAILS_ROUTE = DASHBOARD_ROUTE.chain('/card/details/:cardId');
export const DASHBOARD_CARD_DETAILS_EDIT_ROUTE = DASHBOARD_CARD_DETAILS_ROUTE.chain('/edit');
export const DASHBOARD_CARD_DETAILS_EDIT_NEW_METRIC_ROUTE =
  DASHBOARD_CARD_DETAILS_EDIT_ROUTE.chain('/metric/new');
export const DASHBOARD_CARD_DETAILS_EDIT_SAVED_METRIC_ROUTE =
  DASHBOARD_CARD_DETAILS_EDIT_ROUTE.chain('/metric/edit/:metricId');
export const DASHBOARD_CARD_DETAILS_EDIT_NEW_FUNNEL_ROUTE =
  DASHBOARD_CARD_DETAILS_EDIT_ROUTE.chain('/funnel/new');
export const DASHBOARD_CARD_DETAILS_EDIT_SAVED_FUNNEL_ROUTE =
  DASHBOARD_CARD_DETAILS_EDIT_ROUTE.chain('/funnel/edit/:funnelId');
export const DASHBOARD_CARD_DETAILS_EDIT_NEW_SEGMENT_ROUTE =
  DASHBOARD_CARD_DETAILS_EDIT_ROUTE.chain('/segment/new');
export const DASHBOARD_CARD_DETAILS_EDIT_SAVED_SEGMENT_ROUTE =
  DASHBOARD_CARD_DETAILS_EDIT_ROUTE.chain('/segment/edit/:segmentId');
export const DASHBOARD_CARD_DETAILS_EDIT_CUSTOM_SEGMENT_ROUTE =
  DASHBOARD_CARD_DETAILS_EDIT_ROUTE.chain('/segment/custom');

// Metrics
export const METRICS_ROUTE = ORG_ROUTE.chain('/metrics');
export const METRICS_WITH_HELP_ROUTE = METRICS_ROUTE.chain('/help');
export const METRIC_CREATE_ROUTE = METRICS_ROUTE.chain('/create');
export const METRIC_CREATE_WITH_HASH_ROUTE = METRIC_CREATE_ROUTE.chain('/:hash');
export const METRIC_CREATE_WITH_USER_OVERLAY = METRIC_CREATE_ROUTE.chain('/user');
export const METRIC_CREATE_WITH_HELP_ROUTE = METRIC_CREATE_ROUTE.chain('/help');

export const METRIC_DETAILS_ROUTE = METRICS_ROUTE.chain('/details/:id').queryParams<
  ShowAnnotationParams & DashboardFilterParams
>();
export const METRIC_DETAILS_ROUTE_WITH_USER_OVERLAY = METRIC_DETAILS_ROUTE.chain('/user');
export const METRIC_DETAILS_WITH_HASH_ROUTE = METRIC_DETAILS_ROUTE.chain('/:hash').queryParams<
  ShowAnnotationParams & DashboardFilterParams
>();
export const METRIC_DETAILS_WITH_HELP_ROUTE = METRIC_DETAILS_ROUTE.chain('/help');

// Metric Alerts
export const METRIC_ALERTS_ROUTE = METRIC_DETAILS_ROUTE.chain('/alerts');
export const METRIC_ALERTS_CREATE_ROUTE = METRIC_ALERTS_ROUTE.chain('/create');
export const METRIC_ALERTS_DETAIL_ROUTE =
  METRIC_ALERTS_ROUTE.chain('/:alertConfigId').queryParams<ShowAnnotationParams>();
export const METRIC_ALERTS_EDIT_ROUTE = METRIC_ALERTS_DETAIL_ROUTE.chain('/edit');

export type DashboardFilterParams = {
  /** The dashboard filters to apply when showing the DxObject */
  filters?: string;
};
export type ShowAnnotationParams = {
  annotationId?: string;
};

// Metric Insights
export type FromDashboardQueryParams = {
  fromDashboard?: string;
};
export const METRIC_INSIGHT_ROUTE =
  METRIC_DETAILS_ROUTE.chain('/insight/:insightId').queryParams<FromDashboardQueryParams>();

// Settings
export const SETTINGS_ROUTE = ORG_ROUTE.chain('/settings');
export const SETTINGS_GENERAL = SETTINGS_ROUTE.chain('/general');
export const SETTINGS_RECORDING = SETTINGS_ROUTE.chain('/recording');
export const SETTINGS_CAPTURE = SETTINGS_ROUTE.chain('/capture');
export const SETTINGS_CAPTURED_EVENTS = SETTINGS_ROUTE.chain('/captured-events');
export const SETTINGS_SEARCH = SETTINGS_ROUTE.chain('/search');
export const SETTINGS_PROPERTIES = SETTINGS_ROUTE.chain('/properties');
// Optional ID because the properties base and detail routes both map to the PropertiesSettings component
export const SETTINGS_PROPERTIES_DETAIL = SETTINGS_PROPERTIES.chain<'/:id', { id?: string }>(
  '/:id',
);
export const SETTINGS_PROPERTIES_DETAIL_WITH_HELP = SETTINGS_PROPERTIES_DETAIL.chain('/help');
export const SETTINGS_ELEMENT_PROPERTY_RULES_DETAIL = SETTINGS_PROPERTIES.chain('/rules/:id');
export const SETTINGS_ELEMENT_PROPERTY_RULES_DETAIL_WITH_HELP =
  SETTINGS_ELEMENT_PROPERTY_RULES_DETAIL.chain('/help');
export const SETTINGS_PAGES = SETTINGS_ROUTE.chain('/pages');
export const SETTINGS_EVENTS = SETTINGS_ROUTE.chain('/events');
export const SETTINGS_ELEMENTS = SETTINGS_ROUTE.chain('/elements');
export const SETTINGS_PAGES_DETAIL = SETTINGS_PAGES.chain('/:id');
export const SETTINGS_PAGES_DETAIL_WITH_HELP = SETTINGS_PAGES_DETAIL.chain('/help');
export const SETTINGS_EVENTS_DETAIL = SETTINGS_EVENTS.chain('/:id');
export const SETTINGS_EVENTS_DETAIL_WITH_HELP = SETTINGS_EVENTS_DETAIL.chain('/help');
export const SETTINGS_ELEMENTS_DETAIL = SETTINGS_ELEMENTS.chain('/:id');
export const SETTINGS_ELEMENTS_DETAIL_WITH_HELP = SETTINGS_ELEMENTS_DETAIL.chain('/help');

export const SETTINGS_ERRORS = SETTINGS_ROUTE.chain('/apiErrors');
export const SETTINGS_MOBILE_RECORDING = SETTINGS_ROUTE.chain('/mobile');
export const SETTINGS_PRIVACY = SETTINGS_ROUTE.chain('/privacy');
export const SETTINGS_INTEGRATIONS = SETTINGS_ROUTE.chain('/integrations');
export const SETTINGS_ANYWHERE_ACTIVATIONS = SETTINGS_ROUTE.chain('/activation');
export const SETTINGS_DATA_DESTINATIONS = SETTINGS_ROUTE.chain('/destinations');
export const SETTINGS_DATA_DESTINATIONS_REDIRECT_ROUTE = SETTINGS_ROUTE.chain('/data-destinations');
export const SETTINGS_INTEGRATIONS_EDIT = SETTINGS_INTEGRATIONS.chain('/integration/:id');
export const SETTINGS_DATA_DESTINATIONS_BIGQUERY = SETTINGS_DATA_DESTINATIONS.chain('/bigquery');
export const SETTINGS_DATA_DESTINATIONS_GCS = SETTINGS_DATA_DESTINATIONS.chain('/gcs');
export const SETTINGS_DATA_DESTINATIONS_SNOWFLAKE = SETTINGS_DATA_DESTINATIONS.chain('/snowflake');
export const SETTINGS_DATA_DESTINATIONS_REDSHIFT = SETTINGS_DATA_DESTINATIONS.chain('/redshift');
export const SETTINGS_DATA_DESTINATIONS_S3 = SETTINGS_DATA_DESTINATIONS.chain('/s3');
export const SETTINGS_DATA_DESTINATIONS_BLOB_STORAGE =
  SETTINGS_DATA_DESTINATIONS.chain('/azure-blob-storage');
export const SETTINGS_DATA_DESTINATIONS_WEBHOOKS = SETTINGS_DATA_DESTINATIONS.chain('/webhooks');

export const SETTINGS_DATA_DESTINATIONS_WEBHOOKS_SETTINGS =
  SETTINGS_DATA_DESTINATIONS_WEBHOOKS.chain('/settings');
export const SETTINGS_DATA_DESTINATIONS_WEBHOOKS_STREAMS =
  SETTINGS_DATA_DESTINATIONS_WEBHOOKS.chain('/streams');
export const SETTINGS_DATA_DESTINATIONS_WEBHOOKS_STREAMS_EDIT =
  SETTINGS_DATA_DESTINATIONS_WEBHOOKS_STREAMS.chain('/:streamId');
export const SETTINGS_ANYWHERE_ACTIVATIONS_STREAM_EDIT =
  SETTINGS_ANYWHERE_ACTIVATIONS.chain('/:streamId');
export const SETTINGS_SPEC = SETTINGS_ROUTE.chain('/spec');
export const SETTINGS_SPEC_DETAIL = SETTINGS_SPEC.chain('/:id');

export const SETTINGS_INTEGRATION_USERS = SETTINGS_INTEGRATIONS_EDIT.chain('/users');
export const SETTINGS_API_KEYS = SETTINGS_ROUTE.chain('/apikeys');
export const SETTINGS_WEBHOOKS = SETTINGS_ROUTE.chain('/webhooks');
export const SETTINGS_USERS = SETTINGS_ROUTE.chain('/users');
export const SETTINGS_GROUPS = SETTINGS_ROUTE.chain('/groups');
export const SETTINGS_TEAM = SETTINGS_ROUTE.chain('/team');

export const SETTINGS_DX_CLEANUP_PROTOTYPE = SETTINGS_ROUTE.chain('/cleanup');

export const SETTINGS_SESSIONS = SETTINGS_ROUTE.chain('/sessions');
export const SETTINGS_PROFILE = SETTINGS_ROUTE.chain('/profile');
export const SETTINGS_ACCESSIBILITY = SETTINGS_ROUTE.chain('/accessibility');
export const SETTINGS_NOTIFICATIONS = SETTINGS_ROUTE.chain('/notifications');
export const SETTINGS_BILLING = SETTINGS_ROUTE.chain('/billing');
export const SETTINGS_UMBRELLA = SETTINGS_ROUTE.chain('/umbrella');
export const SETTINGS_SSO = SETTINGS_ROUTE.chain('/sso');
export const SETTINGS_UMBRELLA_USERS = SETTINGS_ROUTE.chain('/umbrellaUsers');
export const SETTINGS_GENAI_FEATURES = SETTINGS_ROUTE.chain('/genai-features');

export const SETTINGS_DATA_HOME = SETTINGS_ROUTE.chain('/overview');

// Playback
export const SESSION_EXPANDER_ROUTE = ORG_ROUTE.chain('/sessionExpander/:sExpr/:continuation?');

const PLAYBACK_BASE_ROUTE = ORG_ROUTE.chain('/session/:sExpr');
export const PLAYBACK_ROUTE = PLAYBACK_BASE_ROUTE.chain('/:continuation?');

export const STATELESS_PLAYBACK_REDIRECT_ROUTE = ORG_ROUTE.chain('/client-session/:sExpr');

export const PLAYBACK_DEV_TOOLS_ROUTE = PLAYBACK_BASE_ROUTE.chain<
  `/${typeof DEVTOOLS_PATH}/:mode?`, // NB(vasilios): Supplied so we can override `Params` with an enum type.
  {
    mode?: DevToolsModeParam;
  }
>(`/${DEVTOOLS_PATH}/:mode?`);

export const PLAYBACK_NETWORK_SIDECAR_ROUTE = PLAYBACK_BASE_ROUTE.chain(
  `/${DEVTOOLS_PATH}/${NETWORK_PATH}/:pageIdx/:entryIdx`,
);

// Embed Playback
export const PLAYBACK_EMBED_ROUTE = ROOT_ROUTE.chain('/embed/:token');
// NOTE: this param must remain synced with the PLAYBACK_DEV_TOOLS_ROUTE above
export const PLAYBACK_EMBED_DEV_TOOLS_ROUTE = PLAYBACK_EMBED_ROUTE.chain<
  `/${typeof DEVTOOLS_PATH}/:mode?`, // NB(vasilios): Supplied so we can override `Params` with an enum type.
  {
    mode?: DevToolsModeParam;
  }
>(`/${DEVTOOLS_PATH}/:mode?`);

export const PLAYBACK_EMBED_NETWORK_SIDECAR_ROUTE = PLAYBACK_EMBED_ROUTE.chain(
  `/${DEVTOOLS_PATH}/${NETWORK_PATH}/:pageIdx/:entryIdx`,
);

// Segments
export const SEGMENTS_BASE_ROUTE = ORG_ROUTE.chain('/segments');
export const SEGMENT_ROUTE = SEGMENTS_BASE_ROUTE.chain('/:segmentId');
export const SEGMENTS_WITH_HELP_ROUTE = SEGMENTS_BASE_ROUTE.chain('/help');
// TODO(vasilios): When upgrading to React Router v6, this route will need to be split into two.
// React Router v6 does not support regex params nor does ExtractRouteParams auto-typing.
export const SEGMENT_SEARCH_ROUTE = SEGMENT_ROUTE.chain<
  '/:viewMode/:search?', // NB(vasilios): react-router's ExtractRouteParams doesn't work with enum-params, so this is a type-hack.
  {
    viewMode: 'people' | 'searchie';
    search?: string;
  }
>('/:viewMode(people|searchie):search?' as '/:viewMode/:search?');
export const SEGMENT_SEARCH_MAIN_ROUTE = SEGMENT_ROUTE.chain('/people:search?');
export const SEGMENT_SEARCH_PAGED_ROUTE = SEGMENT_SEARCH_ROUTE.chain('/:page?');
export const SEGMENT_SEARCH_DETAIL_ROUTE = SEGMENT_ROUTE.chain('/searchie:search?/:searchieId');
export const SEGMENT_SEARCH_DETAIL_PAGED_ROUTE = SEGMENT_SEARCH_DETAIL_ROUTE.chain('/:page?');
export const SEGMENT_SEARCH_DETAIL_SETTINGS_ROUTE =
  SEGMENT_SEARCH_DETAIL_ROUTE.chain(':settings(:.+)?');

// Alerts
export const ALERTS_ROUTE = ORG_ROUTE.chain('/alerts');
export const ALERTS_NEW = SEGMENT_ROUTE.chain('/searchie/SearchieDailyActives/alerts/new');
export const ALERTS_UPDATE = SEGMENT_ROUTE.chain(
  '/searchie/SearchieDailyActives:search?/alerts/:alertId',
);
export const ALERT_NOTIFICATION = ALERTS_UPDATE.chain('/notifications/:notificationId');

// Funnels
export const FUNNELS_ROUTE = ORG_ROUTE.chain('/funnels');
export const FUNNELS_WITH_HELP_ROUTE = FUNNELS_ROUTE.chain('/help');
export const FUNNEL_CREATE_ROUTE = FUNNELS_ROUTE.chain('/create');
export const FUNNEL_CREATE_WITH_HASH_ROUTE = FUNNEL_CREATE_ROUTE.chain('/:hash');
export const FUNNEL_CREATE_WITH_HELP_ROUTE = FUNNEL_CREATE_ROUTE.chain('/help');
export const FUNNEL_CREATE_WITH_USER_OVERLAY = FUNNEL_CREATE_ROUTE.chain('/user');

export const FUNNEL_DETAILS_ROUTE = FUNNELS_ROUTE.chain('/details/:id').queryParams<
  ShowAnnotationParams & DashboardFilterParams
>();
export const FUNNEL_DETAILS_ROUTE_WITH_USER_OVERLAY = FUNNEL_DETAILS_ROUTE.chain('/user');
export const FUNNEL_DETAILS_WITH_HASH_ROUTE = FUNNEL_DETAILS_ROUTE.chain('/:hash').queryParams<
  ShowAnnotationParams & DashboardFilterParams
>();
export const FUNNEL_DETAILS_WITH_HELP_ROUTE = FUNNEL_DETAILS_ROUTE.chain('/help');

// Exports
export const EXPORTS_ROUTE = ORG_ROUTE.chain('/exports');

// Overlays
// User overlay matches paths that end in /user/:userId
export const USER_OVERLAY_ROUTE = ROOT_ROUTE.chain('*/user/:userId');

// Journeys
export const JOURNEYS_ROUTE = ORG_ROUTE.chain('/journeys');
export const JOURNEYS_WITH_HELP_ROUTE = JOURNEYS_ROUTE.chain('/help');
export const JOURNEY_CREATE_ROUTE = JOURNEYS_ROUTE.chain('/create');
export const JOURNEY_CREATE_WITH_HASH_ROUTE = JOURNEY_CREATE_ROUTE.chain('/:hash');
export const JOURNEY_CREATE_WITH_HELP_ROUTE = JOURNEY_CREATE_ROUTE.chain('/help');
export const JOURNEY_DETAILS_ROUTE = JOURNEYS_ROUTE.chain('/details/:id');
export const JOURNEY_DETAILS_WITH_HASH_ROUTE = JOURNEY_DETAILS_ROUTE.chain('/:hash');
export const JOURNEY_DETAILS_WITH_HELP_ROUTE = JOURNEY_DETAILS_ROUTE.chain('/help');

// Detections
export const DETECTIONS_ROUTE = ORG_ROUTE.chain('/detections');
export const DETECTIONS_DETAILS_ROUTE = DETECTIONS_ROUTE.chain('/details/:id');
export const DETECTIONS_WITH_HELP_ROUTE = DETECTIONS_ROUTE.chain('/help');
export const DETECTIONS_DETAILS_WITH_HELP_ROUTE = DETECTIONS_DETAILS_ROUTE.chain('/help');

// Heatmaps
export const HEATMAPS_ROUTE = ORG_ROUTE.chain('/heatmaps');
export const HEATMAPS_WITH_HELP_ROUTE = HEATMAPS_ROUTE.chain('/help');
export const HEATMAP_CREATE_ROUTE = HEATMAPS_ROUTE.chain('/create');
export const HEATMAP_CREATE_WITH_HASH_ROUTE = HEATMAP_CREATE_ROUTE.chain('/:hash');
export const HEATMAP_CREATE_WITH_HELP_ROUTE = HEATMAP_CREATE_ROUTE.chain('/help');

export const HeatmapQueryParamsNames = {
  // An element index of the rank indicator for click or conversion maps to use
  element: 'elem',
  // If true, then the session playlist will be filtered to the selected element
  filter: 'filter',
};
export type HeatmapQueryParams = {
  // An element index of the rank indicator for click or conversion maps to use
  elem?: number;
  // If true, then the session playlist will be filtered to the selected element
  filter?: boolean;
};

export const HEATMAP_DETAILS_ROUTE = HEATMAPS_ROUTE.chain('/details/:id').queryParams<
  HeatmapQueryParams,
  QueryParamValidator<HeatmapQueryParams>
>(heatmapQueryParamValidation);
export const HEATMAP_DETAILS_WITH_USER_OVERLAY = HEATMAP_DETAILS_ROUTE.chain('/user');
export const HEATMAP_DETAILS_WITH_HASH_ROUTE = HEATMAP_DETAILS_ROUTE.chain('/:hash');
export const HEATMAP_DETAILS_WITH_HELP_ROUTE = HEATMAP_DETAILS_ROUTE.chain('/help');

// Retention
export const RETENTION_ROUTE = ORG_ROUTE.chain('/retention');
export const RETENTION_WITH_HELP_ROUTE = RETENTION_ROUTE.chain('/help');
export const RETENTION_CREATE_ROUTE = RETENTION_ROUTE.chain('/create');
export const RETENTION_CREATE_WITH_HASH_ROUTE = RETENTION_CREATE_ROUTE.chain('/:hash');
export const RETENTION_CREATE_WITH_HELP_ROUTE = RETENTION_CREATE_ROUTE.chain('/help');
export const RETENTION_CREATE_WITH_USER_OVERLAY = RETENTION_CREATE_ROUTE.chain('/user');
export const RETENTION_DETAILS_ROUTE = RETENTION_ROUTE.chain('/details/:id');
export const RETENTION_DETAILS_WITH_HASH_ROUTE = RETENTION_DETAILS_ROUTE.chain('/:hash');
export const RETENTION_DETAILS_WITH_HELP_ROUTE = RETENTION_DETAILS_ROUTE.chain('/help');
export const RETENTION_DETAILS_WITH_USER_OVERLAY = RETENTION_DETAILS_ROUTE.chain('/user');

// Virtual Dashboards ... name TBD :)
export const VIRTUAL_DASHBOARDS_ROUTE = ORG_ROUTE.chain('/virtual-dashboards');
// users
export const VIRTUAL_DASHBOARDS_USERS_ROUTE = VIRTUAL_DASHBOARDS_ROUTE.chain('/users');
// errors
export const VIRTUAL_DASHBOARDS_ERRORS_ROUTE = VIRTUAL_DASHBOARDS_ROUTE.chain('/errors');
export const VIRTUAL_DASHBOARDS_UNCAUGHT_EXCEPTION_ROUTE =
  VIRTUAL_DASHBOARDS_ERRORS_ROUTE.chain('/uncaught-exception');
export const VIRTUAL_DASHBOARDS_ERROR_CLICK_ROUTE =
  VIRTUAL_DASHBOARDS_ERRORS_ROUTE.chain('/error-click');
export const VIRTUAL_DASHBOARDS_NETWORK_ERROR_ROUTE =
  VIRTUAL_DASHBOARDS_ERRORS_ROUTE.chain('/network-error');
export const VIRTUAL_DASHBOARDS_CONSOLE_ERROR_ROUTE =
  VIRTUAL_DASHBOARDS_ERRORS_ROUTE.chain('/console-error');
// elements
export const VIRTUAL_DASHBOARDS_ELEMENTS_ROUTE = VIRTUAL_DASHBOARDS_ROUTE.chain('/elements');
export const VIRTUAL_DASHBOARDS_CUSTOM_EVENTS_ROUTE =
  VIRTUAL_DASHBOARDS_ROUTE.chain('/custom-events');
// pages
export const VIRTUAL_DASHBOARDS_PAGES_ROUTE = VIRTUAL_DASHBOARDS_ROUTE.chain('/pages');
export const VIRTUAL_DASHBOARDS_PAGES_TYPE_ROUTE = VIRTUAL_DASHBOARDS_PAGES_ROUTE.chain<
  '/:type', // NB(vasilios): Supplied so we can override `Params` with an enum type.
  {
    type: 'engagement' | 'performance';
  }
>('/:type');

export const VIRTUAL_DASHBOARDS_FUNNELS_ROUTE = VIRTUAL_DASHBOARDS_ROUTE.chain('/funnels');

// Plan Upgrade Page
export const PLAN_UPGRADE_ROUTE = ORG_ROUTE.chain('/upgrade');

// Free Detail Page
export const FREE_DETAIL_ROUTE = ORG_ROUTE.chain('/free-plan');

// Spaces
export const SPACES_ROUTE = ORG_ROUTE.chain('/spaces');

// templates
export const TEMPLATES_ROUTE = ORG_ROUTE.chain('/templates');
export const DASHBOARDS_TEMPLATE_ROUTE = TEMPLATES_ROUTE.chain('/dashboards/:id');

export type SpaceQueryParams = {
  tab?: 'library' | 'home';
  type?: LibraryObjectFilterParamValue;
};
export const SPACE_ROUTE = SPACES_ROUTE.chain('/:id').queryParams<
  SpaceQueryParams,
  QueryParamValidator<SpaceQueryParams>
>(spaceQueryParamValidation);

export const ANNOTATIONS_ROUTE = ORG_ROUTE.chain('/annotations');

// Data Tables
export const DATA_TABLES_ROUTE = ORG_ROUTE.chain('/data-tables');
export const DATA_TABLE_CREATE_ROUTE = DATA_TABLES_ROUTE.chain('/create');
export const DATA_TABLE_CREATE_WITH_HASH_ROUTE = DATA_TABLE_CREATE_ROUTE.chain('/:hash');
export const DATA_TABLE_CREATE_WITH_HELP_ROUTE = DATA_TABLE_CREATE_ROUTE.chain('/help');
export const DATA_TABLE_CREATE_WITH_USER_OVERLAY = DATA_TABLE_CREATE_ROUTE.chain('/user');
export const DATA_TABLE_DETAIL_ROUTE = DATA_TABLES_ROUTE.chain('/details/:id');
export const DATA_TABLE_DETAIL_WITH_HASH_ROUTE = DATA_TABLE_DETAIL_ROUTE.chain('/:hash');
export const DATA_TABLE_DETAIL_WITH_HELP_ROUTE = DATA_TABLE_DETAIL_ROUTE.chain('/help');
export const DATA_TABLE_DETAIL_WITH_USER_OVERLAY = DATA_TABLE_DETAIL_ROUTE.chain('/user');

// Opportunities
export type OpportunitiesDetailQueryParams = {
  legacy?: boolean;
};

export const OPPORTUNITIES_ROUTE = ORG_ROUTE.chain('/opportunities');
export const OPPORTUNITIES_WITH_HELP_ROUTE = OPPORTUNITIES_ROUTE.chain('/help');

// Opportunities tab routes
export const OPPORTUNITIES_OVERVIEW_ROUTE = OPPORTUNITIES_ROUTE.chain('/overview');
export const OPPORTUNITIES_OVERVIEW_WITH_HELP_ROUTE = OPPORTUNITIES_OVERVIEW_ROUTE.chain('/help');

export const OPPORTUNITIES_ISSUES_ROUTE = OPPORTUNITIES_ROUTE.chain('/issues');
export const OPPORTUNITIES_ISSUES_WITH_HELP_ROUTE = OPPORTUNITIES_ISSUES_ROUTE.chain('/help');

// Opportunities details routes
// TODO: MNA-315 remove legacy routes
export const OPPORTUNITIES_ISSUES_LEGACY_ROUTE_OLD = OPPORTUNITIES_ROUTE.chain(
  '/:issueId',
).queryParams<OpportunitiesDetailQueryParams, QueryParamValidator<OpportunitiesDetailQueryParams>>(
  opportunitiesQueryParamValidation,
);
export const OPPORTUNITIES_ISSUES_DETAIL_ROUTE_OLD =
  OPPORTUNITIES_ISSUES_LEGACY_ROUTE_OLD.chain('/:instanceId');
export const OPPORTUNITIES_ISSUES_DETAIL_WITH_HELP_ROUTE_OLD =
  OPPORTUNITIES_ISSUES_DETAIL_ROUTE_OLD.chain('/help');
export const OPPORTUNITIES_ISSUES_DETAIL_TREND_ROUTE_OLD =
  OPPORTUNITIES_ISSUES_DETAIL_ROUTE_OLD.chain('/trend').queryParams<
    OpportunitiesDetailQueryParams,
    QueryParamValidator<OpportunitiesDetailQueryParams>
  >(opportunitiesQueryParamValidation);
export const OPPORTUNITIES_ISSUES_DETAIL_TREND_WITH_HELP_ROUTE_OLD =
  OPPORTUNITIES_ISSUES_DETAIL_TREND_ROUTE_OLD.chain('/help');
export const OPPORTUNITIES_ISSUES_DETAIL_IMPACT_ROUTE_OLD =
  OPPORTUNITIES_ISSUES_DETAIL_ROUTE_OLD.chain('/impact').queryParams<
    OpportunitiesDetailQueryParams,
    QueryParamValidator<OpportunitiesDetailQueryParams>
  >(opportunitiesQueryParamValidation);
export const OPPORTUNITIES_ISSUES_DETAIL_IMPACT_WITH_HELP_ROUTE_OLD =
  OPPORTUNITIES_ISSUES_DETAIL_IMPACT_ROUTE_OLD.chain('/help');
export const OPPORTUNITIES_ISSUES_DETAIL_DIAGNOSTICS_ROUTE_OLD =
  OPPORTUNITIES_ISSUES_DETAIL_ROUTE_OLD.chain('/diagnostic').queryParams<
    OpportunitiesDetailQueryParams,
    QueryParamValidator<OpportunitiesDetailQueryParams>
  >(opportunitiesQueryParamValidation);
export const OPPORTUNITIES_DETAIL_DIAGNOSTICS_WITH_HELP_ROUTE_OLD =
  OPPORTUNITIES_ISSUES_DETAIL_DIAGNOSTICS_ROUTE_OLD.chain('/help');

export const OPPORTUNITIES_ISSUES_LEGACY_ROUTE = OPPORTUNITIES_ISSUES_ROUTE.chain(
  '/:issueId',
).queryParams<OpportunitiesDetailQueryParams, QueryParamValidator<OpportunitiesDetailQueryParams>>(
  opportunitiesQueryParamValidation,
);
export const OPPORTUNITIES_ISSUES_DETAIL_ROUTE =
  OPPORTUNITIES_ISSUES_LEGACY_ROUTE.chain('/:instanceId');
export const OPPORTUNITIES_ISSUES_DETAIL_WITH_HELP_ROUTE =
  OPPORTUNITIES_ISSUES_DETAIL_ROUTE.chain('/help');
export const OPPORTUNITIES_ISSUES_DETAIL_TREND_ROUTE = OPPORTUNITIES_ISSUES_DETAIL_ROUTE.chain(
  '/trend',
).queryParams<OpportunitiesDetailQueryParams, QueryParamValidator<OpportunitiesDetailQueryParams>>(
  opportunitiesQueryParamValidation,
);
export const OPPORTUNITIES_ISSUES_DETAIL_TREND_WITH_HELP_ROUTE =
  OPPORTUNITIES_ISSUES_DETAIL_TREND_ROUTE.chain('/help');
export const OPPORTUNITIES_ISSUES_DETAIL_IMPACT_ROUTE = OPPORTUNITIES_ISSUES_DETAIL_ROUTE.chain(
  '/impact',
).queryParams<OpportunitiesDetailQueryParams, QueryParamValidator<OpportunitiesDetailQueryParams>>(
  opportunitiesQueryParamValidation,
);
export const OPPORTUNITIES_ISSUES_DETAIL_IMPACT_WITH_HELP_ROUTE =
  OPPORTUNITIES_ISSUES_DETAIL_IMPACT_ROUTE.chain('/help');
export const OPPORTUNITIES_ISSUES_DETAIL_DIAGNOSTICS_ROUTE =
  OPPORTUNITIES_ISSUES_DETAIL_ROUTE.chain('/diagnostic').queryParams<
    OpportunitiesDetailQueryParams,
    QueryParamValidator<OpportunitiesDetailQueryParams>
  >(opportunitiesQueryParamValidation);
export const OPPORTUNITIES_DETAIL_DIAGNOSTICS_WITH_HELP_ROUTE =
  OPPORTUNITIES_ISSUES_DETAIL_DIAGNOSTICS_ROUTE.chain('/help');

// Opportunities funnels routes
export const OPPORTUNITIES_FUNNELS_ROUTE = OPPORTUNITIES_ROUTE.chain('/funnels');
export const OPPORTUNITIES_FUNNELS_WITH_HELP_ROUTE = OPPORTUNITIES_FUNNELS_ROUTE.chain('/help');

export const OPPORTUNITIES_FUNNELS_INVESTIGATION_ROUTE = OPPORTUNITIES_FUNNELS_ROUTE.chain(
  '/:funnelId/investigation/:statisticId',
);
export const OPPORTUNITIES_FUNNELS_INVESTIGATION_WITH_HELP_ROUTE =
  OPPORTUNITIES_FUNNELS_INVESTIGATION_ROUTE.chain('/help');

// investigation tabs routes
export const OPPORTUNITIES_FUNNELS_INVESTIGATION_ERRORS_ROUTE =
  OPPORTUNITIES_FUNNELS_INVESTIGATION_ROUTE.chain('/errors');
export const OPPORTUNITIES_FUNNELS_INVESTIGATION_ERRORS_WITH_HELP_ROUTE =
  OPPORTUNITIES_FUNNELS_INVESTIGATION_ERRORS_ROUTE.chain('/help');
export const OPPORTUNITIES_FUNNELS_INVESTIGATION_TRAFFIC_ROUTE =
  OPPORTUNITIES_FUNNELS_INVESTIGATION_ROUTE.chain('/traffic');
export const OPPORTUNITIES_FUNNELS_INVESTIGATION_TRAFFIC_WITH_HELP_ROUTE =
  OPPORTUNITIES_FUNNELS_INVESTIGATION_TRAFFIC_ROUTE.chain('/help');

/** Answers routes */
export const ANSWERS_ROUTE = ORG_ROUTE.chain('/answers');

export const STORY_AI_ROUTE = ORG_ROUTE.chain('/story-ai');
export const VAIDOM_PLAYGROUND_ROUTE = ORG_ROUTE.chain('/vaidom-playground');
